import {
	Box,
	Button,
	Drawer,
	InputAdornment,
	Skeleton,
	TextField,
	Typography,
} from "@mui/material";
import { Clock2, MessageSquareMore, SendHorizonal, User, UserPlus, Users, X } from "lucide-react";
import { URI_API, getHeaders } from "../../utils/config";
import { LoggedUser } from "../../store/interfaces";
import capitalize from "../../utils/capitalize";
import { AppState } from "../../store/reducer";
import { useSelector } from "react-redux";
import "./PatientActivityDrawer.scss";
import { useContext, useState } from "react";
import { PatientsListContext } from "../../components/PatientsList/PatientsList";

export default function PatientActivityDrawer() {
	const [comment, setComment] = useState<string>("");

	const loggedUser: LoggedUser = useSelector((state: AppState) => state.loggedUser);

	const {
		isDrawerOpen,
		toggleDrawer,
		setIsLoading,
		isLoading,
		setSelectedPatient,
		selectedPatient,
		patientComments,
		setForceUpdate,
		forceUpdate,
	} = useContext(PatientsListContext);

	const writeDate = (date: string, time: string) => {
		const [year, month, day] = date.split("-");
		const [hour, min, sec] = time.split(":");
		return day + "/" + month + "/" + year + " a las " + hour + ":" + min;
	};

	const sendPatientActivity = () => {
		setIsLoading(true);
		fetch(
			`${URI_API}/comment/`,
			getHeaders("POST", {
				patient_id: selectedPatient.id,
				activity_type: "comment",
				description: comment,
			}),
		).catch((error) => {
			console.log(error);
		});
		setForceUpdate(forceUpdate + 1);
		setComment("");
	};

	return (
		<Drawer
			open={isDrawerOpen}
			onClose={toggleDrawer(false)}
			anchor="right"
			sx={{ width: "30%" }}
		>
			<Box className="drawer">
				<Box className="drawer__header">
					<img
						className="drawer__header-grain"
						src={require("../../assets/images/grain.png")}
						alt="background"
					/>
					<Box className="drawer__header__title-container">
						<MessageSquareMore
							size={35}
							className="drawer__header__title-container-icon"
						/>
						<Box>
							<Typography className="drawer__header__title-container__text-title">
								Actividad del paciente
							</Typography>
							<Typography className="drawer__header__title-container__text-subtitle">
								Consulta la actividad y movimientos del paciente.
							</Typography>
						</Box>
						<Button
							className="drawer__header-button"
							onClick={toggleDrawer(false)}
						>
							<X size={30} />
						</Button>
					</Box>
				</Box>
				<Box className="drawer__info">
					<Box className="drawer__info__label">
						<Typography>Paciente:</Typography>
						<Typography>Cama:</Typography>
						<Typography>Profesional:</Typography>
						<Typography>Profesional Auxiliar:</Typography>
					</Box>
					<Box className="drawer__info__data">
						<Typography>{capitalize(selectedPatient?.name)}</Typography>
						<Typography>{selectedPatient?.room}</Typography>
						{selectedPatient?.nursename !== null ? (
							<Box className="drawer__info__data__container">
								<User
									color="#3F6A98"
									size={18}
								/>
								<Typography className="drawer__info__data__container-nurse">
									{capitalize(selectedPatient?.nursename)}
								</Typography>
							</Box>
						) : (
							<Typography>Sin profesional asignado</Typography>
						)}
						{selectedPatient?.secondarynursename !== null ? (
							<Box className="drawer__info__data__container">
								<Users
									color="#2E672F"
									size={18}
								/>
								<Typography className="drawer__info__data__container-secondary-nurse">
									{capitalize(selectedPatient?.secondarynursename)}
								</Typography>
							</Box>
						) : (
							<Typography>Sin profesional asignado</Typography>
						)}
					</Box>
				</Box>
				<Box className="drawer-line" />
				<Box className="drawer__bottom">
					<Box className="drawer__bottom__activity">
						{!isLoading &&
							patientComments.map((comment: any, index: any) => {
								const isInitial = index === patientComments.length - 1;
								const isCategoryChange = !isInitial && comment.comment === "" && comment.prevCategory !== "";

								return (
									<Box className="drawer__bottom__activity__container" key={index}>
										{isInitial ? (
											<UserPlus
												color="#6D247A"
												size={20}
											/>
										) : comment.comment === "" ? (
											<Clock2
												color="#6D247A"
												size={20}
											/>
										) : (
											<MessageSquareMore
												color="#6D247A"
												size={20}
											/>
										)}
										<Box className="drawer__bottom__activity__container__paragraph">
											<Typography className="drawer__bottom__activity__container__paragraph-instruction">
												{comment.user}
												{isInitial
													? " ingresó al paciente:"
													: comment.comment !== ""
													? " comentó:"
													: isCategoryChange
													? " cambió de categoría:"
													: " cambió de turno:"}
											</Typography>
											{comment.comment === "" ? (
												<>
													{isCategoryChange ? (
														<>
															<Typography className="drawer__bottom__activity__container__paragraph-info">
																Categoría inicial: {comment.prevCategory}
															</Typography>
															<Typography className="drawer__bottom__activity__container__paragraph-info">
																Nueva categoría: {comment.category}
															</Typography>
															{comment.nursename !== "" && (
																<Typography className="drawer__bottom__activity__container__paragraph-info">
																	Profesional: {comment.nursename}
																</Typography>
															)}
															{comment.secondaryNursename !== "" &&
															comment.secondaryNursename !== "None" && (
																<Typography className="drawer__bottom__activity__container__paragraph-info">
																	Profesional Auxiliar: {comment.secondaryNursename}
																</Typography>
															)}
														</>
													) : (
														<>
															{comment.category !== "" && comment.category !== "None" && (
																<Typography className="drawer__bottom__activity__container__paragraph-info">
																	Categoría: {comment.category}
																</Typography>
															)}
															<Typography className="drawer__bottom__activity__container__paragraph-info">
																Profesional:{" "}
																{comment.nursename !== ""
																	? `${comment.nursename}`
																	: "Sin profesional asignado"}
															</Typography>
															<Typography className="drawer__bottom__activity__container__paragraph-info">
																Profesional Auxiliar:{" "}
																{comment.secondaryNursename !== "" &&
																comment.secondaryNursename !== "None"
																	? `${comment.secondaryNursename}`
																	: "Sin profesional auxiliar asignado"}
															</Typography>
														</>
													)}
												</>
											) : (
												<Typography className="drawer__bottom__activity__container__paragraph-info">
													{comment.comment}
												</Typography>
											)}
											<Typography className="drawer__bottom__activity__container__paragraph-time">
												{writeDate(comment.date, comment.time)}
											</Typography>
										</Box>
									</Box>
								);
							})}
						{isLoading && (
							<Box className="drawer__bottom__activity__container">
								<Skeleton
									variant="circular"
									width={20}
									height={20}
								/>
								<Box className="drawer__bottom__activity__container__paragraph">
									{Array.from({ length: 5 }).map((e: any) => {
										return (
											<Skeleton
												width={400}
												variant="text"
												sx={{ fontSize: "16px" }}
											/>
										);
									})}
								</Box>
							</Box>
						)}
					</Box>
				</Box>
				<Box className="drawer__bottom__comment">
					<Box className="drawer__bottom__comment-fade" />
					<Box className="drawer__bottom__comment__content">
						<TextField
							multiline
							maxRows={5}
							size="small"
							className="drawer__bottom__comment__content-text-field"
							type="text"
							placeholder="Escribe un comentario aquí."
							onChange={(e) => setComment(e.target.value)}
							value={comment}
						/>
						<Button
							className="drawer__bottom__comment__content-button"
							disabled={comment !== undefined && comment?.length <= 0}
							onClick={sendPatientActivity}
						>
							<Typography>Enviar</Typography>
							<SendHorizonal size={50} />
						</Button>
					</Box>
				</Box>
			</Box>
		</Drawer>
	);
}
